import { ChangeDetectionStrategy, Component } from '@angular/core';
import { deepmerge } from '@library/charts/utils';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { Home } from '@library/store/homes/homes.interface';
import { MeasuresFacade } from '@library/store/measures/measures.facade';
import { EnumApiScales, EnumMeasureTypes, EnumScales, Measures, TimeRange } from '@library/store/measures/measures.interface';
import { UserFacade } from '@library/store/user/user.facade';
import { getColorValue } from '@library/utils/helpers/functions/getColorVariable';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { ToDisplayValuePipe } from '@library/utils/pipes/to-display-value.pipe';
import { ToUserUnitPipe } from '@library/utils/pipes/to-user-unit.pipe';
import { GraphTooltipService } from '@molecules/graph-tooltip/graph-tooltip.service';
import { YScaleType } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { ControlPageGraphTooltipService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/control-page-graph-tooltip.service';
import { SPAN_GAPS, baseConfig } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/graph-configs/common-config';
import { AutoYMinMaxPlugin } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/plugins/auto-y-min-max';
import { VerticalLinePlugin } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/plugins/vertical-line';
import { ControlPageStore, DatasetCard, GraphModes } from '@pages/control-page/control-page-store.service';
import { DATASET_BASE_OPTIONS, SCALE_UNITS } from '@pages/control-page/control-page.const';
import { ChartConfiguration } from 'chart.js';
import { DeepPartial } from 'chart.js/types/utils';
import { combineLatest, filter, firstValueFrom, map, switchMap } from 'rxjs';

const windSpeedIcon = new Image();
windSpeedIcon.src = '/assets/images/wind/wind-direction.svg';

interface Graph {
  scale: EnumApiScales;
  timeRange: TimeRange;
  config: DeepPartial<ChartConfiguration>;
}

@Component({
  selector: 'app-control-page-graph',
  templateUrl: './control-page-graph.component.html',
  styleUrls: ['./control-page-graph.component.scss'],
  providers: [{provide: GraphTooltipService, useClass: ControlPageGraphTooltipService}],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ControlPageGraphComponent {

  hasNoData = false;

  graph: Graph;

  private loading$ = this.homesFacade.currentHomeId$.pipe(
    switchMap(() => this.controlPageStore.state$),
    switchMap(state => combineLatest(state.card?.datasets[state.options.graphMode].map(d => {
      const scale = this.controlPageStore.scaleToApiScale(state.scale);

      if (d.home) {
        return this.measuresFacade.getHomeLoadStatus(d.home.id, scale);
      }
      else if (d.module) {
        return this.measuresFacade.getModuleLoadStatus(d.module.id, scale);
      }
      else if (d.room) {
        return this.measuresFacade.getRoomLoadStatus(d.room.id, scale);
      }
    }) ?? [])),
    map(datasets => datasets.some(loading => loading))
  );

  private config$ = combineLatest({
    state: this.controlPageStore.state$.pipe(isDefined()),
    home: this.homesFacade.currentHome$.pipe(isDefined()),
  }).pipe(
    filter(({state: {card}}) => !!card),
    switchMap(({state: {card, scale, options}, home}) => this.buildConfig(card, options.graphMode, scale, home))
  );

  private apiScale$ = this.controlPageStore.scale$.pipe(
    map(scale => this.controlPageStore.scaleToApiScale(scale)),
  );

  viewModel$ = combineLatest({
    loading: this.loading$,
    config: this.config$,
    apiScale: this.apiScale$,
    scale: this.controlPageStore.scale$,
    timeRange: this.controlPageStore.timeRange$,
  });

  constructor(
    private controlPageStore: ControlPageStore,
    private measuresFacade: MeasuresFacade,
    private homesFacade: HomesFacade,
    private toUserUnitPipe: ToUserUnitPipe,
    private userFacade: UserFacade,
    private graphTooltipService: GraphTooltipService,
    private toDisplayValuePipe: ToDisplayValuePipe,
  ) { }

  buildConfig(card: DatasetCard, graphMode: GraphModes, scale: EnumScales, home: Home) {
    const cardDatasets = card.datasets[graphMode];
    const moduleDatasets = cardDatasets.filter(d => d.module);
    const roomDatasets = cardDatasets.filter(d => d.room);

    const apiScale = this.controlPageStore.scaleToApiScale(scale);

    return combineLatest({
      modulesMeasures: this.measuresFacade.getModulesMeasures(moduleDatasets.map(m => m.module)),
      roomsMeasures: this.measuresFacade.getRoomsMeasures(roomDatasets.map(m => m.room)),
      homeMeasures: this.measuresFacade.getHomeMeasures(home),
    }).pipe(
      switchMap(async ({homeMeasures, modulesMeasures, roomsMeasures}) => {
        const datasets = cardDatasets.map((cardDataset) => {
          let data: Measures = [];
          if (cardDataset.home) {
            data = homeMeasures[cardDataset.measureType]?.[apiScale] ?? [];
          }
          else if (cardDataset.module) {
            data = modulesMeasures[moduleDatasets.indexOf(cardDataset)][cardDataset.measureType]?.[apiScale] ?? [];
          }
          else if (cardDataset.room) {
            data = roomsMeasures[roomDatasets.indexOf(cardDataset)][cardDataset.measureType]?.[apiScale] ?? [];
          }

          data = data.map(m => ({...m, y: this.toUserUnitPipe.transform(m.y, cardDataset.measureType)}));

          return {
            ...DATASET_BASE_OPTIONS[cardDataset.type],
            data: data,
            yAxisID: cardDataset.yScaleType,
            borderColor: getColorValue(cardDataset.color),
            backgroundColor: getColorValue(cardDataset.color),
            hoverBackgroundColor: getColorValue(cardDataset.color),
            spanGaps: SPAN_GAPS[apiScale],
            hidden: cardDataset.hidden,
            order: cardDataset.type.includes('bar') ? 1 : 0,
          };
        });

        this.hasNoData = this.isEmpty(datasets.map(dataset => dataset.data));

        const user = await firstValueFrom(this.userFacade.currentUser$);
        const currencySymbol = await firstValueFrom(this.homesFacade.currencySymbol$);

        const config = deepmerge(baseConfig, {
          data: {
            datasets: datasets,
          },
          options: {
            scales: {
              x: {type: scale, offset: false, stacked: true},
            },
            plugins: {
              tooltip: this.graphTooltipService.tooltip,
              'auto-y-min-max': {
                unitSystem: user.unit_system ?? 0,
                unitPressure: user.unit_pressure ?? 0,
                unitWind: user.unit_wind ?? 0,
              }
            }
          },
          plugins: [AutoYMinMaxPlugin, VerticalLinePlugin]
        });

        delete config.options.scales.y;

        const toDisplayValuePipe = this.toDisplayValuePipe;

        const yScales = [...new Set(cardDatasets.map(m => m.yScaleType))];
        yScales.forEach((type, i) => {
          config.options.scales[type] = {
            type: type,
            position: i % 2 === 0 ? 'left' : 'right',
            grid: { drawOnChartArea: i === 0 },
            // Use stacked only on Elec and Price for elec graphs
            stacked: [YScaleType.ELECTRICITY, YScaleType.PRICE].includes(type),
            ticks: {
              callback: function (val) {
                if (type === YScaleType.ELECTRICITY) {
                  const value = toDisplayValuePipe.transform(val, EnumMeasureTypes.SUM_ENERGY_ELEC_BASE);
                  return `${value} ${SCALE_UNITS[this.type](user, currencySymbol)}`;
                }
                return `${this.getLabelForValue(val)} ${SCALE_UNITS[this.type](user, currencySymbol)}`;
              }
            }
          };
        });

        return config;
      }),
    );
  }

  isEmpty = arr => Array.isArray(arr) && arr.every(this.isEmpty);

}
